import { isCloudinaryUrl, resizeImage, setAutoFormat } from '@micromart/utils/lib/cloudinary'

/** Display size for product photos in assistant markdown tables. */
export const ASSISTANT_TABLE_IMAGE_PX = 56

const MARKDOWN_IMAGE = /!\[([^\]]*)\]\(([^)]+)\)/

export function assistantTableImageUrl(url: string, displayPx = ASSISTANT_TABLE_IMAGE_PX): string {
	return setAutoFormat(resizeImage(url, displayPx * 2, displayPx * 2, true))
}

function parseMarkdownTableRow(line: string): string[] {
	return line.split('|').slice(1, -1).map((cell) => cell.trim())
}

function formatMarkdownTableRow(cells: string[]): string {
	return `| ${cells.join(' | ')} |`
}

function isTableSeparator(line: string): boolean {
	return /^\|[\s\-:|]+\|$/.test(line.trim())
}

function extractMarkdownImage(cell: string): string | null {
	const match = cell.match(MARKDOWN_IMAGE)
	return match ? match[0] : null
}

function setMarkdownImage(cell: string, image: string | null): string {
	const without = cell.replace(MARKDOWN_IMAGE, '').trim()
	if (!image) return without
	return without ? `${without} ${image}` : image
}

/**
 * LLMs often put the first product thumbnail in the header row, shifting every
 * image down one row (Skittles appears under "Image" header). Shift images down
 * so each row matches its product, and use plain "Image" in the header.
 */
export function fixAssistantTableImageColumnShift(content: string): string {
	const lines = content.split('\n')
	const out: string[] = []
	let i = 0

	while (i < lines.length) {
		const line = lines[i]
		if (!line.trim().startsWith('|')) {
			out.push(line)
			i += 1
			continue
		}

		const block: string[] = []
		while (i < lines.length && lines[i].trim().startsWith('|')) {
			block.push(lines[i])
			i += 1
		}

		if (block.length < 3 || !isTableSeparator(block[1])) {
			out.push(...block)
			continue
		}

		const headerCells = parseMarkdownTableRow(block[0])
		const imageColIdx = headerCells.findIndex((cell) => MARKDOWN_IMAGE.test(cell))
		if (imageColIdx === -1) {
			out.push(...block)
			continue
		}

		let carryImage = extractMarkdownImage(headerCells[imageColIdx])
		headerCells[imageColIdx] = 'Image'
		const fixedBlock = [formatMarkdownTableRow(headerCells), block[1]]

		for (let rowIdx = 2; rowIdx < block.length; rowIdx += 1) {
			const cells = parseMarkdownTableRow(block[rowIdx])
			if (cells.length <= imageColIdx) {
				fixedBlock.push(block[rowIdx])
				continue
			}
			const rowImage = extractMarkdownImage(cells[imageColIdx])
			cells[imageColIdx] = setMarkdownImage(cells[imageColIdx], carryImage)
			carryImage = rowImage
			fixedBlock.push(formatMarkdownTableRow(cells))
		}

		out.push(...fixedBlock)
	}

	return out.join('\n')
}

/** Rewrite Cloudinary URLs in markdown image syntax before HTML conversion. */
export function resizeAssistantContentImages(content: string): string {
	return content.replace(
		MARKDOWN_IMAGE,
		(full, alt: string, url: string) => {
			if (!isCloudinaryUrl(url)) return full
			return `![${alt}](${assistantTableImageUrl(url)})`
		},
	)
}

/** Remove stray product images from table header cells after HTML render. */
export function sanitizeAssistantTableHeaderImages(html: string): string {
	return html.replace(/<th\b([^>]*)>([\s\S]*?)<\/th>/gi, (full, attrs: string, inner: string) => {
		if (!/<img\b/i.test(inner)) return full
		const withoutImg = inner.replace(/<img\b[^>]*>/gi, '').trim()
		const label = withoutImg || 'Image'
		return `<th${attrs}>${label}</th>`
	})
}

/** Rewrite Cloudinary img src after marked renders HTML (covers raw HTML from the model). */
export function resizeAssistantHtmlImages(html: string): string {
	return html.replace(
		/(<img\b[^>]*\bsrc=["'])([^"']+)(["'][^>]*>)/gi,
		(full, prefix: string, url: string, suffix: string) => {
			if (!isCloudinaryUrl(url)) return full
			return `${prefix}${assistantTableImageUrl(url)}${suffix}`
		},
	)
}

/** Full markdown → HTML pipeline for assistant message bodies. */
export function prepareAssistantMarkdownHtml(content: string, parseMarkdown: (md: string) => string): string {
	let markdown = fixAssistantTableImageColumnShift(content)
	markdown = resizeAssistantContentImages(markdown)
	const html = parseMarkdown(markdown)
	return sanitizeAssistantTableHeaderImages(resizeAssistantHtmlImages(html))
}
